/**
 * 会话主页
 * @author sunzhiguang
 * @date 2020/6/16
 */

import React, { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./main.less";
import ChatLeft from "@/components/chat/view/LeftSide";
import ChatRight from "@/components/chat/view/RightSide";
import ChatMain from "@/components/chat/view/Main";
import { connect } from "dva";
import { useHistory, useLocation, withRouter } from "react-router-dom";
import SidebarSet, { ISetSwitch } from "@/components/chat/SidebarSet";
import ChatState, { UserState } from "@/types/chat/State";
import ContentFrame from "@/components/ContentFrame";
import log from "@/utils/logger";
import { Employee, Session, GroupRoster, SessionType } from "@/types/chat";
import config, { ChatConfig } from "@/config/config";
import ChatEvent from "@/components/chat/view/ChatEvent";
import { MessageNotifyType } from "@/types/chat/enum";
import Context from "@/components/chat/context";
import { getGroupRoster, getGroupRosterIdentity } from "@/utils/chat/group";
import { DrawTypeEnum } from "@/components/chat/enum";
import { usePrevious } from "@/utils/chat/index";
import { getGroupInfo } from "@/api/egroup";
import { AI_NOTICE_EXCLUDE_PINS } from "@/contant/chat";
import { convertEmployeeToString } from "@/utils/chat/message";
import ImService from "@/server/ImService";
import { FocusSDK, isFocusEnv } from "@/utils";
import { isAIChatSession } from "@/utils/chat/session";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import {
  getCurrentFileControls,
  getCurrentFileSendControls,
  getFileSecurityTagList,
  getDefaultFileTag
} from "@/utils/tools";
import { message } from "antd";

interface ChatProps {
  type?: string;
  history: any;
  openSetup: boolean;
  match: any;
  clientConfig: any;
  teamConfig: any;
  customAppList: Session[];
  quickReplys: any[];
}

interface DvaProps {
  getCurrentEmployee: Function;
  initSelectedSession: Function;
  launchSingleSession: Function;
  launchGroupSession: Function;
  launchNoticeSession: Function;
  launchOrgNoticeSession: Function;
  changeNotifyType: Function;
  setglobalDrawerVisible: Function;
  setmessageDrawerVisible: Function;
  setStartMessageId: Function;
  setIsMessageModule: Function;
  getLLMTagsList: Function;
  getLLMAgentList: Function;
  updateSelectSessionInfo: Function;
}

type IChatProps = Readonly<ChatProps & DvaProps & UserState & ChatState>;

function Chat(props: IChatProps) {
  const {
    administrator,
    sub_administrator,
    ordinary,
    match,
    selectedSession,
    currentEmployee,
    launchSingleSession,
    launchGroupSession,
    launchNoticeSession,
    launchOrgNoticeSession,
    initSelectedSession,
    loadAllSessionListEnd,
    setglobalDrawerVisible,
    messageDrawerVisible,
    userData,
    setStartMessageId,
    sessionMessageMap,
    clientConfig,
    teamConfig,
    customAppList,
    getLLMTagsList,
    getLLMAgentList,
    updateSelectSessionInfo,
    quickReplys
  } = props;
  const { appRuntimeEnv } = React.useContext<GlobalContextConfig>(GlobalContext);
  const { im } = appRuntimeEnv;

  const preSessionId = usePrevious(selectedSession?.sessionId || "");
  // sunxiaolin3 进入会话框时初始化新建任务  （bug:新建任务时，点击消息推送，进入对话页面后新建任务没有关闭）
  const groupRosters = useMemo(() => {
    if (!selectedSession.isGroup || administrator.length === 0) {
      return [];
    }
    return getGroupRoster(administrator, sub_administrator, ordinary);
  }, [selectedSession, administrator, sub_administrator, ordinary]);

  const groupRosterIdentity = useMemo(() => {
    if (!selectedSession.isGroup || !currentEmployee) {
      return "";
    }
    if ((administrator || []).length === 0) {
      return "";
    }
    return getGroupRosterIdentity(administrator, sub_administrator, currentEmployee);
  }, [selectedSession, administrator, sub_administrator, currentEmployee]);

  const mountedRef = useRef(false);
  const [openSetup, setOpenSetup] = React.useState(false);
  const [historySearchOpen, setSearchRecordOpen] = React.useState(false);
  const [shareJoyspaceOpen, setShareJoyspaceOpen] = React.useState(false);
  const windowWidthRef = useRef(0);
  useEffect(() => {
    if (!selectedSession || !selectedSession.sessionId) {
      setOpenSetup(false);
    }
  }, [selectedSession]);

  let sessionMessages: any[] = sessionMessageMap[selectedSession.sessionId];
  const lastMessage = useMemo(() => {
    const last = (sessionMessages || []).slice(-1)[0] || {};
    return last;
  }, [sessionMessages]);

  const lastAIMessage = useMemo(() => {
    if (isAIChatSession(selectedSession)) {
      if (!lastMessage) {
        return null;
      }
      if ((lastMessage as any)?.predictType === "CHAT" && !(lastMessage as any)?.isMyself) {
        return lastMessage;
      }
    } else {
      return null;
    }
  }, [lastMessage, selectedSession]);

  const isLLMAI = useMemo(() => {
    return (clientConfig?.llmAI || 0) === 1 && isFocusEnv();
  }, [clientConfig?.llmAI]);

  useEffect(() => {
    if (isLLMAI) {
      getLLMAgentList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLLMAI]);

  const assistantAI = useMemo(() => {
    if (!isFocusEnv() || FocusSDK.isPrivateNet()) {
      return {
        isAssistantAI: 0
      };
    }
    const { quickPhrases = 0, aiAssociation = 0 } = clientConfig;
    return {
      isAssistantAI: quickPhrases || aiAssociation ? 1 : 0,
      quickPhrases,
      aiAssociation
    };
  }, [clientConfig]);
  const quickReply = useMemo(() => {
    return appRuntimeEnv.config.quickReply || {};
  }, [appRuntimeEnv]);

  const userQuickReply = useMemo(() => {
    return clientConfig.quickReply === 0 ? 0 : 1;
  }, [clientConfig.quickReply]);

  const aggregateDuration = useMemo(() => {
    return im.aggregateDuration || 0;
  }, [im]);

  // 文件控制
  const fileTagList = useMemo(() => {
    const conf = getFileSecurityTagList(teamConfig);
    return conf || [];
  }, [teamConfig]);

  // 当前文件控制
  const currentFileControl = useMemo(() => {
    const { loginType = "normal" } = userData || {};
    const conf = getCurrentFileControls(teamConfig, loginType);
    return conf || [];
  }, [teamConfig, userData]);

  // 当前文件发送控制
  const currentFileSendControl = useMemo(() => {
    const conf = getCurrentFileSendControls({
      teamConfig,
      controlList: currentFileControl
    });
    return conf || [];
  }, [teamConfig, currentFileControl]);

  const defaultFileTag = useMemo(() => {
    const id = getDefaultFileTag(fileTagList);
    return id;
  }, [fileTagList]);

  const aiMergeOptions = useMemo(() => {
    const opts = {
      keywords: (teamConfig?.aiMergeKeywords || "").split(","),
      duration: Number(teamConfig?.aiMergeDuration || "3600000"),
      whitelist: (teamConfig?.aiMergeWhiteList || "").split(",")
    };
    return opts;
  }, [teamConfig]);

  useEffect(() => {
    if (messageDrawerVisible) {
      setglobalDrawerVisible(false);
      setOpenSetup(false);
      setSearchRecordOpen(false);
    }
  }, [messageDrawerVisible, setglobalDrawerVisible]);

  useEffect(() => {
    if (selectedSession?.sessionId !== preSessionId && historySearchOpen) {
      setSearchRecordOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession.sessionId, preSessionId, historySearchOpen]);

  const history = useHistory();
  const [sid, setSid] = useState("");
  const [sType, setSType] = useState("");
  const [app, setApp] = useState("");
  const [tid, setsTid] = useState("");
  const [sMid, setSMid] = useState(-1);
  const [sText, setSText] = useState("");
  const [sTimestamp, setSTimestamp] = useState(0);
  const [top, setTop] = useState("");
  const [secret, setSecret] = useState("");
  const [orgType, setOrgType] = useState("");
  const [sessionList, setsessionList] = useState<Session>();
  const pSid = usePrevious(sid);
  const pTid = usePrevious(tid);
  const pAid = usePrevious(app);
  const pSMid = usePrevious(sMid);

  const setParams = useCallback(
    ({
      sid = "",
      sType = "",
      aid = "",
      tid = "",
      top = "",
      sMid = "-1",
      sText = "",
      sTimestamp = "0",
      secret = "",
      type = ""
    }) => {
      setSid(sid);
      setSType(sType);
      setApp(aid);
      setsTid(tid);
      setTop(top);
      setSMid(parseInt(sMid, 10));
      setSText(sText);
      setSTimestamp(parseInt(sTimestamp, 10));
      setSecret(secret);
      setOrgType(type);
    },
    []
  );

  useEffect(() => {
    const { sid, sType } = match.params;
    const query = new URLSearchParams(history.location.search);
    const aid = query.get("app") || "";
    const tid = query.get("tid") || "";
    const top = query.get("top") || "";
    const sMid = query.get("sMid") || -1;
    const sText = query.get("sText") || "";
    const sTimestamp = query.get("sTimestamp") || 0;
    const secret = query.get("secret") || "";
    const type = query.get("type") || "";
    const orgType = query.get("orgType") || "";
    setParams({
      sid: sid || "",
      sType: sType || "",
      aid,
      tid,
      top,
      sMid,
      sText,
      sTimestamp,
      secret,
      type,
      orgType
    });
  }, [history, match.params, setParams]);

  // eslint-disable-next-line complexity
  useEffect(() => {
    // 处理联系人跳转会话页面 并选中指定联系人的会员 在会列表最前端
    if (!loadAllSessionListEnd || !sid) {
      return;
    }
    // 修复【Bug IM】左侧回话列表有多个相同的单聊对话窗口--hujun
    if (!currentEmployee?.app) {
      return;
    }
    if (pSMid === sMid && pSid === sid && pTid === tid && pAid === app) {
      return;
    }
    if (sMid > -1) {
      setStartMessageId({ sMid, sText, sTimestamp });
    } else {
      setStartMessageId({ sMid: -1, skipAnchorId: {} });
    }
    if (sid.includes("sub_")) {
      launchOrgNoticeSession({
        user: {
          app,
          pin: sid?.split(":")[0],
          teamId: tid
        },
        top,
        type: orgType
      });
    } else if (sType === "g" && sid) {
      launchGroupSession({ sid, top, sText, sMid, sTimestamp });
    } else if (sType === "s" && sid && tid) {
      if (sid === "@im.jd.com") {
        return;
      }
      // 单聊密聊会话跳转
      const type = secret === "true" ? SessionType.SECRET_SINGLE : SessionType.SINGLE;
      launchSingleSession({
        top,
        user: {
          app,
          pin: sid?.split(":")[0],
          teamId: tid
        },
        type,
        sText,
        sMid,
        sTimestamp
      });
    } else if (sType === "n" && sid) {
      let _sid = sid;
      let flag = true;
      for (const pin of AI_NOTICE_EXCLUDE_PINS) {
        if (sid.indexOf(pin) > -1) {
          flag = false;
        }
      }
      // 过滤自定义应用独立会话
      if (
        customAppList.findIndex((item) => sid.indexOf((item.info as Employee).userId) > -1) > -1
      ) {
        flag = false;
      }
      if (flag) {
        _sid = `aiRobot:dd.notice:focus.bot:${convertEmployeeToString(currentEmployee)}`;
      }
      launchNoticeSession({ sid: _sid, top });
    } else {
      initSelectedSession({ selectedSession: selectedSession || {} });
    }
    // changeNotifyType({ selectedNotifyType: MessageNotifyType.ALL });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pSid,
    pTid,
    pAid,
    sid,
    app,
    tid,
    sType,
    top,
    loadAllSessionListEnd,
    currentEmployee,
    selectedSession,
    sMid,
    sText,
    sTimestamp,
    customAppList,
    orgType
  ]);

  const showSessionList = config[ChatConfig.CHAT_SESSION_LIST_ENABLE];

  function handleResize() {
    windowWidthRef.current = document.body.clientWidth;
  }

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    mountedRef.current = true;
    return () => {
      window.removeEventListener("resize", handleResize);
      mountedRef.current = false;
    };
  }, []);

  // 监听离开消息页面
  useEffect(() => {
    return () => {
      if (!history?.location.pathname.match(/messages/)) {
        ImService?.getInstance()?.setCurrentSession("");
      }
    };
  }, [history?.location]);

  const onOp = useCallback(
    (session: any) => {
      if (session) {
        const sessions = {
          ...session,
          sessionId: selectedSession.sessionId
        };
        setsessionList(sessions);
      }
    },
    [selectedSession]
  );
  // useEffect(() => {
  //     const chatManager = imService.getChatManager();
  //     if (chatManager) {
  //         chatManager.on('session-update', onOp);
  //         return () => {
  //             chatManager.off('session-update', onOp);
  //         };
  //     }
  // }, [imService, onOp]);

  function handleMainClick() {
    if (openSetup) {
      setOpenSetup(false);
      setSearchRecordOpen(false);
      setShareJoyspaceOpen(false);
    }
    setTimeout(() => {
      ChatEvent.closeMention();
    }, 100);
  }

  async function onToggleDraw(type: DrawTypeEnum) {
    if (!mountedRef.current) return;
    switch (type) {
      case DrawTypeEnum.setup: {
        try {
          const result = await getGroupInfo({
            groupIds: [{ groupId: selectedSession?.gid }]
          });
          const groupInfo = result?.groups?.[0];
          updateSelectSessionInfo({
            session: {
              info: groupInfo
            }
          });
        } catch (e) {
          message.error(e);
        }
        setOpenSetup(!openSetup);
        setSearchRecordOpen(false);
        setShareJoyspaceOpen(false);
        break;
      }
      case DrawTypeEnum.search:
        setOpenSetup(false);
        setSearchRecordOpen(!historySearchOpen);
        setShareJoyspaceOpen(false);
        break;
      case DrawTypeEnum.shareJoyspace:
        setOpenSetup(false);
        setSearchRecordOpen(false);
        setShareJoyspaceOpen(!shareJoyspaceOpen);
        break;
    }
  }

  function closeSetting() {
    if (!mountedRef.current) return;
    setOpenSetup(false);
  }

  // const ImDonwloadPanel = useMemo(() => {
  //   return <ImCollapsePanel gid="im" />;
  // }, []);

  return (
    <Context.Provider
      value={{
        currentEmployee,
        groupRosters,
        groupRosterIdentity,
        userData,
        onToggleSetup: onToggleDraw,
        closeSetting,
        lastAIMessage,
        lastMessage,
        isLLMAI,
        assistantAI,
        httpMatchUrls: im.httpMatchList || ["mp.weixin.qq.com", "smart.swj.beijing.gov.cn"],
        blackUrls: im.blackUrlList || [],
        quickReplyConfig: quickReply,
        userQuickReply: userQuickReply,
        aggregateDuration,
        fileTagList,
        currentFileControl,
        currentFileSendControl,
        defaultFileTag,
        aiMergeOptions
      }}
    >
      <ContentFrame
        hasGap={true}
        leftSide={showSessionList && <ChatLeft />}
        rightSide={
          <div
            className={[
              "chat-container",
              openSetup ? "open" : "",
              !config[ChatConfig.CHAT_RIGHT_SIDER_ENABLE] ? "hidden-right-side" : ""
            ].join(" ")}
          >
            {!selectedSession.sessionId && <div className="main-top" />}
            <div
              className="main"
              style={{
                top: selectedSession.sessionId ? "0px" : "60px"
              }}
              onClick={handleMainClick}
            >
              <ChatMain onToggleSetup={onToggleDraw} />
            </div>
            {config[ChatConfig.CHAT_RIGHT_SIDER_ENABLE] && (
              <div className="right">
                <ChatRight
                  session={sessionList}
                  openSetup={openSetup}
                  historySearchOpen={historySearchOpen}
                  shareJoyspaceOpen={shareJoyspaceOpen}
                  onToggleSetup={onToggleDraw}
                  onClickClose={() => {
                    onToggleDraw(DrawTypeEnum.search);
                  }}
                />
              </div>
            )}
          </div>
        }
      />
      {/* {ImDonwloadPanel} */}
    </Context.Provider>
  );
}

function mapStateToProps({ chat, user: { userData, clientConfig, teamConfig } }: any) {
  const {
    administrator,
    sub_administrator,
    ordinary,
    selectedSession,
    currentEmployee,
    loadAllSessionListEnd,
    globalDrawerVisible,
    messageDrawerVisible,
    allSessionList,
    sessionMessageMap,
    customAppList,
    quickReplys
  } = chat as ChatState;

  return {
    administrator,
    sub_administrator,
    ordinary,
    selectedSession,
    currentEmployee,
    loadAllSessionListEnd,
    globalDrawerVisible,
    messageDrawerVisible,
    sessionMessageMap,
    userData: userData ? userData.user : {},
    clientConfig: clientConfig || {},
    allSessionList,
    customAppList,
    teamConfig: teamConfig || {},
    quickReplys
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    getCurrentEmployee: () => {
      dispatch({ type: "chat/getCurrentEmployee", payload: {} });
    },
    initSelectedSession(data: { selectedSession: Session }) {
      dispatch({ type: "chat/initSelectedSession", payload: data });
    },
    launchSingleSession(data: { employee: Employee }) {
      dispatch({ type: "chat/launchSingleSession", payload: data });
    },
    launchGroupSession(data: { sid: string }) {
      dispatch({ type: "chat/launchGroupSession", payload: data });
    },
    launchNoticeSession(data: { sid: string }) {
      dispatch({ type: "chat/launchNoticeSession", payload: data });
    },
    launchOrgNoticeSession(data: any) {
      dispatch({ type: "chat/launchOrgNoticeSession", payload: data });
    },
    changeNotifyType(data: { selectedNotifyType: MessageNotifyType }) {
      dispatch({ type: "chat/changeNotifyType", payload: data });
    },
    setglobalDrawerVisible(data: boolean) {
      dispatch({ type: "chat/setglobalDrawerVisible", payload: data });
    },
    setmessageDrawerVisible(data: boolean) {
      dispatch({ type: "chat/setmessageDrawerVisible", payload: data });
    },
    setStartMessageId(data: { sMid: number; sText: string; sTimestamp: number }) {
      dispatch({ type: "chat/setStartMessageId", payload: data });
    },
    getLLMTagsList() {
      dispatch({ type: "chat/getLLMTagsList", payload: {} });
    },
    getLLMAgentList() {
      dispatch({ type: "chat/getLLMAgentList", payload: {} });
    },
    updateSelectSessionInfo(data: any) {
      dispatch({ type: "chat/updateSelectSessionInfo", payload: data });
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Chat as any));
